import { DateTime } from 'luxon';

import { useOperationVersions } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { ResolvedUser } from '@/shared/components/activity';
import { ActivityTimeline } from '@/shared/components/activity/activity-timeline';

type FileUploadedChange = {
  kind?: 'report' | 'followup-protocol' | 'attachment';
  filename?: string | null;
};

export function OperationActivity({ operationId }: { operationId: string }) {
  const { t, locale } = useIntl();

  const {
    data: activityData,
    isLoading: isLoadingActivities,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useOperationVersions(operationId, { disabled: !operationId, limit: 15 });

  if (isLoadingActivities) {
    return <div className="text-center text-muted-foreground">{t('activity.loading')}</div>;
  }

  if (!activityData?.pages[0].docs.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const allActivities = activityData.pages.flatMap((page) => page.docs);
  if (!allActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const filteredActivities = allActivities.filter((activity) => {
    if (activity.type === 'created') return !hasNextPage;
    return true;
  });

  if (!filteredActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const getFieldName = (field: string) => {
    const key = `activity.operation.fields.${field}` as unknown as never;
    const translated = t(key) as unknown as string;
    return translated || field;
  };

  const resolveValue = (value: unknown, field?: string) => {
    if (value === null || value === undefined || value === '') return t('activity.none');

    if (typeof value === 'string' || typeof value === 'number') {
      const stringValue = String(value);

      if (field === 'status') {
        const statusKey = `operations.status.${stringValue}` as unknown as never;
        const statusLabel = t(statusKey) as unknown as string;
        return statusLabel || stringValue;
      }

      if (field === 'followUpDecision') {
        const decisionKey = `operations.followUpDecision.${stringValue}` as unknown as never;
        const decisionLabel = t(decisionKey) as unknown as string;
        return decisionLabel || stringValue;
      }

      return stringValue;
    }

    return JSON.stringify(value);
  };

  return (
    <div className="space-y-4">
      <ActivityTimeline
        activities={filteredActivities.map((activity) => ({
          id: activity.id,
          type: activity.type as 'created' | 'updated',
          timestamp: activity.timestamp,
          user: activity.user,
          changes: activity.changes,
        }))}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      >
        {(activity) =>
          activity.type === 'created' ? (
            <div className="flex items-center gap-1">
              <ResolvedUser userId={activity.user} />
              <span className="text-muted-foreground">{t('activity.operation.created')}</span>
              <span className="text-muted-foreground">
                · {DateTime.fromISO(activity.timestamp).toRelative({ locale })}
              </span>
            </div>
          ) : (
            activity.changes?.map((change, index) => {
              if (change.field === 'fileUploaded') {
                const payload = (change.to || {}) as FileUploadedChange;
                const kind = payload.kind || 'attachment';
                const kindLabelKey = `activity.operation.fileKinds.${kind}` as unknown as never;
                const kindLabel = (t(kindLabelKey) as unknown as string) || kind;
                const fileLabel = payload.filename
                  ? `${kindLabel}: ${payload.filename}`
                  : kindLabel;

                return (
                  <div key={index} className="flex items-center gap-1">
                    <ResolvedUser userId={activity.user} />
                    <span className="text-muted-foreground">
                      {t('activity.operation.fileUploaded')}
                    </span>
                    <span className="font-medium">{fileLabel}</span>
                    <span className="text-muted-foreground">
                      · {DateTime.fromISO(activity.timestamp).toRelative({ locale })}
                    </span>
                  </div>
                );
              }

              return (
                <div key={index} className="flex items-center gap-1">
                  <ResolvedUser userId={activity.user} />
                  <span className="text-muted-foreground">{t('activity.changed')}</span>
                  <span className="font-medium">{getFieldName(change.field)}</span>
                  <span className="text-muted-foreground">{t('activity.from')}</span>
                  <span className="font-medium">{resolveValue(change.from, change.field)}</span>
                  <span className="text-muted-foreground">{t('activity.to')}</span>
                  <span className="font-medium">{resolveValue(change.to, change.field)}</span>
                  <span className="text-muted-foreground">
                    · {DateTime.fromISO(activity.timestamp).toRelative({ locale })}
                  </span>
                </div>
              );
            })
          )
        }
      </ActivityTimeline>
    </div>
  );
}
